import React, { useState, useRef, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { Stack } from "@mui/material";
import { Mic, Pause, Play, Square, Trash2 } from "lucide-react";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  recorderRoot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    padding: 12,
    borderRadius: 10,
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid rgba(255, 255, 255, 0.07)",
  },
  recordBtn: {
    width: "100%",
    borderRadius: "10px !important",
    textTransform: "none",
    fontWeight: 700,
    color: "#e9edef !important",
    background: "#1d222b !important",
    border: "1px solid rgba(255, 255, 255, 0.10)",
    boxShadow: "none !important",
    padding: "9px 12px !important",
    "&:hover": {
      background: "#242a35 !important",
      borderColor: "rgba(255, 255, 255, 0.16)",
      boxShadow: "none !important",
    },
    "& .MuiButton-startIcon, & .MuiButton-startIcon svg": {
      color: theme.palette.primary.main,
      stroke: theme.palette.primary.main,
    },
  },
  visualizerContainer: {
    width: "100%",
    height: 54,
    backgroundColor: "#11151b",
    borderRadius: 10,
    border: "1px solid rgba(255, 255, 255, 0.08)",
    overflow: "hidden",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  barsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    height: "100%",
    padding: "0 12px",
  },
  bar: {
    width: 3,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 2,
    transition: "height 0.1s ease",
  },
  barPaused: {
    backgroundColor: "rgba(233, 237, 239, 0.55)",
  },
  timerRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  timer: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: "1rem",
    fontWeight: 600,
    color: "#e9edef",
    minWidth: 60,
    textAlign: "center",
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    animation: "$pulse 1s ease-in-out infinite",
  },
  statusDotRecording: {
    backgroundColor: theme.palette.primary.main,
  },
  statusDotPaused: {
    backgroundColor: "rgba(233, 237, 239, 0.55)",
    animation: "none",
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.4, transform: "scale(0.8)" },
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  controlBtn: {
    width: 36,
    height: 36,
    borderRadius: "10px",
    padding: 0,
    color: "#e9edef",
    backgroundColor: "#1d222b",
    border: "1px solid rgba(255, 255, 255, 0.10)",
    "&:hover": {
      backgroundColor: "#242a35",
    },
  },
  pauseBtn: {
    color: theme.palette.primary.main,
  },
  resumeBtn: {
    color: theme.palette.primary.main,
  },
  stopBtn: {
    color: theme.palette.primary.main,
  },
  cancelBtn: {
    color: "#b8c1cc",
  },
  statusLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#b8c1cc",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    color: "#8f9aa7",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    margin: "2px 0 8px",
  },
  nativeAudioPreview: {
    width: "100%",
    height: 36,
    display: "block",
    borderRadius: 18,
    overflow: "hidden",
  },
}));

const NUM_BARS = 32;

/**
 * AudioRecorder - Componente de gravacao de audio com visualizacao,
 * pause/resume e preview.
 *
 * Props:
 *   onRecorded(blob, file) - chamado quando o usuario finaliza a gravacao
 */
const AudioRecorder = ({ onRecorded }) => {
  const classes = useStyles();

  const [status, setStatus] = useState("idle"); // idle | recording | paused | done
  const [elapsed, setElapsed] = useState(0);
  const [barHeights, setBarHeights] = useState(new Array(NUM_BARS).fill(4));
  const [audioUrl, setAudioUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopEverything();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopEverything = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    mediaRecorderRef.current = null;
    analyserRef.current = null;
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Visualizer animation
  const animateBars = useCallback(() => {
    if (!analyserRef.current) return;
    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const draw = () => {
      if (!analyserRef.current) return;
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      const step = Math.floor(dataArray.length / NUM_BARS);
      const heights = [];
      for (let i = 0; i < NUM_BARS; i++) {
        const val = dataArray[i * step] || 0;
        // map 0-255 to 4-50 px height
        heights.push(Math.max(4, (val / 255) * 50));
      }
      setBarHeights(heights);
    };
    draw();
  }, []);

  const updatePausedPreview = () => {
    if (!chunksRef.current.length) return;
    const blob = new Blob(chunksRef.current, {
      type: mediaRecorderRef.current?.mimeType || "audio/ogg",
    });
    if (blob.size > 0) setAudioUrl(URL.createObjectURL(blob));
  };

  const handleStart = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // Audio analyser for visualization
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // MediaRecorder - prefer ogg/opus (WhatsApp native), fallback to webm
      let mimeType = "audio/webm";
      if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
        mimeType = "audio/ogg;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      }
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
        if (recorder.state === "paused") updatePausedPreview();
      };

      recorder.start(200); // collect data every 200ms for smooth stop
      setStatus("recording");
      setElapsed(0);
      setAudioUrl(null);
      startTimer();
      animateBars();
    } catch (err) {
      console.error("Erro ao acessar microfone:", err);
      alert(i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.microphoneError"));
    }
  };

  const handlePause = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      mediaRecorderRef.current.requestData();
      stopTimer();
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      // Show flat bars when paused
      setBarHeights(new Array(NUM_BARS).fill(8));
      setStatus("paused");
      setTimeout(updatePausedPreview, 120);
    }
  };

  const handleResume = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      startTimer();
      animateBars();
      setAudioUrl(null);
      setStatus("recording");
    }
  };

  const handleStop = () => {
    if (!mediaRecorderRef.current) return;

    const recorder = mediaRecorderRef.current;

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType });

      if (blob.size < 5000) {
        setStatus("idle");
        stopEverything();
        return;
      }

      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setStatus("done");

      // Stop mic + analyser but keep the blob
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      analyserRef.current = null;
    };

    stopTimer();
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    recorder.stop();
  };

  const handleCancel = () => {
    stopTimer();
    stopEverything();
    setBarHeights(new Array(NUM_BARS).fill(4));
    setElapsed(0);
    setAudioUrl(null);
    setStatus("idle");
  };

  const handleConfirm = () => {
    if (!chunksRef.current.length) return;
    // Always save as .ogg so backend detects it as audio
    const blob = new Blob(chunksRef.current, { type: "audio/ogg" });
    const file = new File([blob], `${Date.now()}.ogg`, { type: "audio/ogg" });
    if (onRecorded) onRecorded(blob, file);
    handleCancel();
  };

  const handleDiscard = () => {
    handleCancel();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  // ---------- IDLE ----------
  if (status === "idle") {
    return (
      <div>
        <div className={classes.divider}>
          <span>{i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.or")}</span>
        </div>
        <Button
          className={classes.recordBtn}
          variant="contained"
          startIcon={<Mic size={16} />}
          onClick={handleStart}
        >
          {i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.recordAudio")}
        </Button>
      </div>
    );
  }

  // ---------- DONE (preview) ----------
  if (status === "done") {
    return (
      <div className={classes.recorderRoot}>
        <Typography
          className={classes.statusLabel}
          style={{ color: "#b8c1cc" }}
        >
          {i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.recordingCompleted", {
            time: formatTime(elapsed),
          })}
        </Typography>

        {audioUrl && (
          <audio
            className={classes.nativeAudioPreview}
            src={audioUrl}
            controls
            preload="metadata"
          />
        )}

        <Stack direction="row" gap="8px" justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<Trash2 size={14} />}
            onClick={handleDiscard}
            size="small"
          >
            {i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.discard")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            size="small"
          >
            {i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.useThisAudio")}
          </Button>
        </Stack>
      </div>
    );
  }

  // ---------- RECORDING / PAUSED ----------
  const isRecording = status === "recording";
  const isPaused = status === "paused";

  return (
    <div className={classes.recorderRoot}>
      {/* Status label */}
      <Stack direction="row" alignItems="center" gap="6px">
        <div
          className={`${classes.statusDot} ${
            isRecording
              ? classes.statusDotRecording
              : classes.statusDotPaused
          }`}
        />
        <Typography
          className={classes.statusLabel}
        >
          {isRecording
            ? i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.recording")
            : i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.paused")}
        </Typography>
      </Stack>

      {/* Visualizer bars */}
      <div className={classes.visualizerContainer}>
        <div className={classes.barsContainer}>
          {barHeights.map((h, i) => (
            <div
              key={i}
              className={`${classes.bar} ${isPaused ? classes.barPaused : ""}`}
              style={{ height: h }}
            />
          ))}
        </div>
      </div>

      {/* Timer */}
      <span className={classes.timer}>{formatTime(elapsed)}</span>

      {isPaused && audioUrl && (
        <audio
          className={classes.nativeAudioPreview}
          src={audioUrl}
          controls
          preload="metadata"
        />
      )}

      {/* Controls */}
      <div className={classes.controlsRow}>
        {/* Cancel */}
        <IconButton
          className={`${classes.controlBtn} ${classes.cancelBtn}`}
          onClick={handleCancel}
          title={i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.cancel")}
          size="small"
        >
          <Trash2 size={16} />
        </IconButton>

        {/* Pause / Resume */}
        {isRecording ? (
          <IconButton
            className={`${classes.controlBtn} ${classes.pauseBtn}`}
            onClick={handlePause}
            title={i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.pause")}
          >
            <Pause size={16} />
          </IconButton>
        ) : (
          <IconButton
            className={`${classes.controlBtn} ${classes.resumeBtn}`}
            onClick={handleResume}
            title={i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.resume")}
          >
            <Play size={16} />
          </IconButton>
        )}

        {/* Stop / Finish */}
        <IconButton
          className={`${classes.controlBtn} ${classes.stopBtn}`}
          onClick={handleStop}
          title={i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.finish")}
        >
          <Square size={15} />
        </IconButton>
      </div>

      <Typography variant="caption" style={{ color: "#999" }}>
        {isPaused
          ? i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.pausedHint")
          : i18n.t("flowBuilderModals.singleBlockModal.audioRecorder.recordingHint")}
      </Typography>
    </div>
  );
};

export default AudioRecorder;
